import React, { useState } from 'react';
import {
    Typography,
    Button,
    Input,
    Select,
    Option,
    Chip,
    Textarea,
    IconButton,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
} from "@material-tailwind/react";
import {
    Search,
    MoreVertical,
    Eye,
    Edit,
    UserCheck,
    AlertCircle,
    XCircle,
    MessageSquare,
    Paperclip,
    User,
    Mail,
    Phone,
    Tag,
    TrendingUp,
    RefreshCw,
    Settings,
    Plus,
    Zap,
    AlertTriangle,
    Info,
    X,
    Filter,
    Users,
    PlusIcon,
    UserIcon,
    TagIcon,
    Calendar
} from "lucide-react";
import { useAssignTicket, useCreateTicketByAdmin, useGetAllTickets, useUpdateTicketDueDate, useUpdateTicketStatus } from '../../custom-hooks/use-support';
import { useGetUsers } from '../../custom-hooks/use-users';
import { LEVELS_ENUM, STATIC_USER_ROLES, SUPPORT_TICKET_CATEGORIES_KEY_VALUE_ARRAY, SUPPORT_TICKET_STATUSES } from '../../utils/enums';
import { ModalX, PageHeader } from '../../components';
import { SupportTicketStatusColumns } from '../../utils/ui-interpreters';
import Autocomplete from '../../components/autocomplete';
import { convertToPresentableText } from '../../utils/functions';
import { SUPPORT_TICKET_CATEGORIES_DESCRIPTION } from '../../utils/enums_descriptions';
import moment from 'moment';
import { Link } from 'react-router-dom';

moment.updateLocale('en-short', {
    relativeTime: {
        future: '%s', // no "in"
        past: '%s',   // no "ago"
        s: '1s',
        ss: '%ss',
        m: '1m',
        mm: '%dm',
        h: '1h',
        hh: '%dh',
        d: '1d',
        dd: '%dd',
        M: '1mo',
        MM: '%dmo',
        y: '1y',
        yy: '%dy',
    }
});


const INITIAL_NEW_TICKET_FORM_DATA = {
    customer: null,
    subject: '',
    description: '',
    category: '',
    priority: '',
    // attachments: [] // TODO: Add attachments
}

const SupportTicketsKanban = () => {
    const { data: tickets, isLoading, error, refetch: refetchTickets } = useGetAllTickets({
        _page: 1,
        _limit: 500
    });

    // TODO: Reduce these apis to one api call
    // TODO: Add pagination to the users api - DYNAMIC SEARCH
    const { data: agents, isLoading: agentsLoading, error: agentsError, } = useGetUsers({
        role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE],
        _page: 1,
        _limit: 1000
    });
    const { data: customers, isLoading: customersLoading, error: customersError } = useGetUsers({
        // role: [STATIC_USER_ROLES.USER, STATIC_USER_ROLES.TEAM_MEMBER],
        _page: 1,
        _limit: 1000
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [assignedAgentFilter, setAssignedAgentFilter] = useState('all');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [resolution, setResolution] = useState('');
    const [selectedAgent, setSelectedAgent] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [createTicketModalOpen, setCreateTicketModalOpen] = useState(false);
    const [newTicketFormData, setNewTicketFormData] = useState(INITIAL_NEW_TICKET_FORM_DATA);
    const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
    const [dueDate, setDueDate] = useState(null);

    const updateTicketStatusMutation = useUpdateTicketStatus();
    const assignTicketMutation = useAssignTicket();
    const createTicketByAdminMutation = useCreateTicketByAdmin();
    const updateTicketDueDateMutation = useUpdateTicketDueDate();

    // Status columns configuration
    const statusColumns = Object.keys(SupportTicketStatusColumns).map((status) => {
        const statusConfig = SupportTicketStatusColumns[status];
        return {
            id: status,
            title: statusConfig.label,
            ...statusConfig
        }
    })

    // Filter tickets
    const getFilteredTickets = () => {
        let filtered = tickets?.tickets || [];

        if (searchTerm) {
            filtered = filtered.filter(ticket =>
                ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket._id.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (priorityFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
        }

        if (categoryFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.category === categoryFilter);
        }

        if (assignedAgentFilter !== 'all') {
            if (assignedAgentFilter === 'unassigned') {
                filtered = filtered.filter(ticket => !ticket.assignedAgent);
            } else {
                filtered = filtered.filter(ticket => ticket.assignedAgent?._id === assignedAgentFilter);
            }
        }

        return filtered;
    };

    // Get tickets by status
    const getTicketsByStatus = (status) => {
        return getFilteredTickets().filter(ticket => ticket.status === status);
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: 'text-green-600',
            medium: 'text-orange-600',
            high: 'text-red-600',
            urgent: 'text-purple-600'
        };
        return colors[priority] || 'text-gray-600';
    };

    const getPriorityBg = (priority) => {
        const colors = {
            low: 'bg-green-100',
            medium: 'bg-orange-100',
            high: 'bg-red-100',
            urgent: 'bg-purple-100'
        };
        return colors[priority] || 'bg-gray-100';
    };

    const getPriorityIcon = (priority) => {
        const icons = {
            low: Info,
            medium: AlertTriangle,
            high: AlertCircle,
            urgent: Zap
        };
        return icons[priority] || Info;
    };

    const getCategoryIcon = (category) => {
        const icons = {
            technical: Settings,
            billing: TrendingUp,
            general: MessageSquare,
            account: User,
            bug_report: XCircle,
            feature_request: Plus,
            other: Tag
        };
        return icons[category] || Tag;
    };

    const getTimeAgo = (date) => {
        return moment(date).fromNow();
    };

    const handleViewTicket = (ticket) => {
        setSelectedTicket(ticket);
        setViewDialogOpen(true);
    };

    const handleUpdateStatus = (ticket) => {
        setSelectedTicket(ticket);
        setNewStatus(ticket.status);
        setResolution(ticket.resolution || '');
        setStatusDialogOpen(true);
    };

    const handleAssignTicket = (ticket) => {
        setSelectedTicket(ticket);
        setSelectedAgent(ticket.assignedAgent?._id || '');
        setAssignDialogOpen(true);
    };

    const handleSetDueDate = (ticket) => {
        setSelectedTicket(ticket);
        setDueDate(ticket.dueDate);
        setDueDateDialogOpen(true);
    };

    const submitStatusUpdate = async () => {
        if (!newStatus) return;

        try {
            const payload = {
                status: newStatus,
                resolution: resolution || ''
            };

            await updateTicketStatusMutation.mutateAsync({ ticketId: selectedTicket._id, data: payload });
            setStatusDialogOpen(false);
            setNewStatus('');
            setResolution('');
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const submitAssignment = async () => {
        if (!selectedAgent) return;

        try {
            const payload = {
                agentId: selectedAgent
            };

            await assignTicketMutation.mutateAsync({ ticketId: selectedTicket._id, data: payload });
            setAssignDialogOpen(false);
            setSelectedAgent('');
        } catch (error) {
            console.error('Error assigning ticket:', error);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setPriorityFilter('all');
        setCategoryFilter('all');
        setAssignedAgentFilter('all');
    };

    const hasActiveFilters = searchTerm || priorityFilter !== 'all' || categoryFilter !== 'all' || assignedAgentFilter !== 'all';

    const TicketCard = ({ ticket }) => {
        const PriorityIcon = getPriorityIcon(ticket.priority);
        const CategoryIcon = getCategoryIcon(ticket.category);

        const dueDate = ticket.dueDate ? moment(ticket.dueDate).format('MMM Do, YY') : null;
        const ifDueDateIsPast = dueDate ? moment(dueDate).isBefore(moment()) : false;
        const dueDateColor = [
            SUPPORT_TICKET_STATUSES.RESOLVED,
            SUPPORT_TICKET_STATUSES.CLOSED,
            SUPPORT_TICKET_STATUSES.CANCELLED
        ].includes(ticket.status) ? 'gray' : (ifDueDateIsPast ? 'red' : 'blue');

        const dueDateBgColor = ifDueDateIsPast ? 'bg-red-100' : 'bg-blue-100';

        return (
            <div key={ticket._id} className="bg-white hover:bg-gray-50 border border-gray-200 rounded-lg p-4 mb-3 hover:border-gray-300 transition-all duration-300 cursor-pointer">
                {/* Header */}
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <PriorityIcon className={`h-4 w-4 ${getPriorityColor(ticket.priority)}`} />
                        <CategoryIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-xs font-mono text-gray-500 uppercase">{ticket._id.slice(ticket._id.length - 5, ticket._id.length)}</span>
                        {
                            ticket.dueDate && (
                                <Chip
                                    value={`Due: ${dueDate}`}
                                    className={`px-2 py-1 rounded text-xs font-medium capitalize ${dueDateBgColor} ${dueDateColor} cursor-pointer`}
                                    variant='ghost'
                                    size='sm'
                                    color={dueDateColor} />
                            )}
                    </div>
                    <Menu>
                        <MenuHandler>
                            <IconButton variant="text" size="sm" className="h-6 w-6">
                                <MoreVertical className="h-3 w-3" />
                            </IconButton>
                        </MenuHandler>
                        <MenuList className="border border-gray-200">
                            <Link to={`/support/${ticket._id}`} target="_blank">
                                <MenuItem className="flex items-center gap-2 text-sm">
                                    <Eye className="h-4 w-4" />
                                    View Details
                                </MenuItem>
                            </Link>
                            <MenuItem className="flex items-center gap-2 text-sm" onClick={() => handleUpdateStatus(ticket)}>
                                <Edit className="h-4 w-4" />
                                Update Status
                            </MenuItem>
                            <MenuItem className="flex items-center gap-2 text-sm" onClick={() => handleAssignTicket(ticket)}>
                                <UserCheck className="h-4 w-4" />
                                Assign Agent
                            </MenuItem>
                            {
                                [SUPPORT_TICKET_STATUSES.OPEN, SUPPORT_TICKET_STATUSES.IN_PROGRESS].includes(ticket.status) && (
                                    <MenuItem className="flex items-center gap-2 text-sm" onClick={() => handleSetDueDate(ticket)}>
                                        <Calendar className="h-4 w-4" />
                                        Set Due Date
                                    </MenuItem>
                                )
                            }
                        </MenuList>
                    </Menu>
                </div>

                {/* Subject */}
                <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 leading-tight hover:underline" onClick={() => handleViewTicket(ticket)}>
                    {ticket.subject}
                </h3>

                {/* Customer */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-xs">
                            {ticket.customer.name.charAt(0)}
                        </span>
                    </div>
                    <span className="text-xs text-gray-600 truncate capitalize">
                        {ticket.customer.name}
                    </span>
                </div>

                {/* Priority & Category */}
                <div className="flex items-center gap-2 mb-3">
                    <Chip
                        value={ticket.priority}
                        className={`px-2 py-1 rounded text-xs font-medium capitalize ${getPriorityBg(ticket.priority)} ${getPriorityColor(ticket.priority)} cursor-pointer`}
                    />
                    <Chip
                        value={ticket.category.replace('_', ' ')}
                        className="px-2 py-1 rounded text-xs font-medium capitalize" variant='outlined' color='teal' />
                </div>

                <div className='flex items-center gap-2 mb-1'>
                    {ticket?.attachments?.length > 0 && (
                        <div className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{ticket.attachments.length}</span>
                        </div>
                    )}
                    {ticket?.requiresCustomerAcknowledgment && (
                        <AlertCircle className="h-3 w-3 text-orange-500" />
                    )}
                    {ticket?.escalationLevel > 0 && (
                        <TrendingUp className="h-3 w-3 text-red-500" />
                    )}
                </div>
                <div className="text-xs text-gray-500">
                    {ticket.resolutionDate ? (
                        <span className="text-green-500">
                            {getTimeAgo(ticket.resolutionDate)} - Resolved
                        </span>
                    ) : (
                        <span>
                            Last Activity: {getTimeAgo(ticket.lastUpdated)}
                        </span>
                    )}
                </div>

                <hr className='my-2' />
                {/* Footer */}
                <div className="flex items-center justify-between border-gray-100">
                    <div className="text-xs text-gray-500">
                        <span>
                            {getTimeAgo(ticket.createdDate)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-400">
                        {/* Assigned Agent */}
                        {ticket.assignedAgent ? (
                            <div className="flex items-center gap-2">
                                <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                                    <span className="text-green-600 font-medium text-xs">
                                        {ticket.assignedAgent.name.charAt(0)}
                                    </span>
                                </div>
                                <span className="text-xs text-gray-600">
                                    {ticket.assignedAgent.name}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-400" />
                                <span className="text-xs text-gray-400">Unassigned</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const StatusColumn = ({ column, tickets }) => {
        const ColumnIcon = column.icon;

        return (
            <div className="flex-1 min-w-80">
                <div className={`bg-white border border-gray-200 rounded-lg ${column.bgColor} h-full max-h-[800px] overflow-y-auto relative`}>
                    {/* Column Header */}
                    <div className={`p-4 border-b ${column.borderColor} sticky top-0 bg-white z-10`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <ColumnIcon className={`h-5 w-5 text-${column.color}-600`} />
                                <div>
                                    <h3 className="font-semibold text-gray-900">{column.title}</h3>
                                    <p className="text-sm text-gray-600">{tickets?.length} tickets</p>
                                </div>
                            </div>
                            <div className={`px-2 py-1 rounded-full text-sm font-medium bg-${column.color}-100 text-${column.color}-700`}>
                                {tickets?.length}
                            </div>
                        </div>
                    </div>

                    {/* Column Body */}
                    <div className="p-4 h-100 overflow-y-auto">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                            </div>
                        ) : tickets.length === 0 ? (
                            <div className="text-center py-8">
                                <ColumnIcon className={`h-12 w-12 text-${column.color}-200 mx-auto mb-3`} />
                                <p className="text-sm text-gray-500">No tickets</p>
                                <p className="text-xs text-gray-400">{column.description}</p>
                            </div>
                        ) : (
                            tickets.map((ticket) => (
                                <TicketCard key={ticket._id} ticket={ticket} />
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    const handleCreateTicket = () => {
        setCreateTicketModalOpen(true);
    };

    const handleCreateTicketSubmit = async () => {
        try {
            const payload = {
                customerId: newTicketFormData.customer.value,
                subject: newTicketFormData.subject,
                description: newTicketFormData.description,
                category: newTicketFormData.category,
                priority: newTicketFormData.priority,
            };
            await createTicketByAdminMutation.mutateAsync(payload);
            setCreateTicketModalOpen(false);
            setNewTicketFormData(INITIAL_NEW_TICKET_FORM_DATA);
        } catch (error) {
            console.error('Error creating ticket:', error);
        }
    };

    const handleCreateTicketInputChange = (e) => {
        setNewTicketFormData({ ...newTicketFormData, [e.target.name]: e.target.value });
    };

    const handleCreateTicketSelectChange = (name, value) => {
        setNewTicketFormData({ ...newTicketFormData, [name]: value });
    };

    const submitSetDueDate = async () => {
        try {
            await updateTicketDueDateMutation.mutateAsync({ ticketId: selectedTicket._id, data: { dueDate } });
            setDueDateDialogOpen(false);
            setDueDate(null);
        } catch (error) {
            console.error('Error setting due date:', error);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full space-y-6">
                <PageHeader
                    title="Support Tickets"
                    isLoading={isLoading}
                    actionOnClick={handleCreateTicket}
                    actionType="add"
                    actionText="Create Ticket"
                    customAction={
                        <div className="flex items-center justify-end gap-3">
                            <Button
                                variant="filled"
                                size="sm"
                                className="flex items-center gap-2 rounded-full px-2 md:px-4"
                                onClick={handleCreateTicket}
                                color="blue"
                            >
                                <PlusIcon className="h-4 w-4" />
                                <span className="hidden md:block">Create Ticket</span>
                            </Button>
                            <Button
                                variant="outlined"
                                size="sm"
                                className="flex items-center gap-2 border-gray-300 rounded-full px-2 md:px-4"
                                onClick={() => refetchTickets()}
                                disabled={isLoading}
                            >
                                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                                <span className="hidden md:block">Refresh</span>
                            </Button>
                            <Button
                                variant="filled"
                                size="sm"
                                className="flex items-center gap-2 rounded-full px-2 md:px-4"
                                onClick={() => setShowFilters(!showFilters)}
                                color={hasActiveFilters ? "blue" : "gray"}
                            >
                                <Filter className="h-4 w-4" />
                                <span className="hidden md:block">Filters</span>
                            </Button>
                        </div>
                    }
                />

                {/* Quick Stats */}
                {/* <div className="grid grid-cols-5 gap-4">
                    {statusColumns.map((column) => {
                        const count = getTicketsByStatus(column.id).length;
                        return (
                            <div key={column.id} className="bg-white border border-gray-200 rounded-lg p-4">
                                <div className="flex items-center gap-3">
                                    <column.icon className={`h-5 w-5 text-${column.color}-600`} />
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{column.title}</p>
                                        <p className="text-2xl font-bold text-gray-900">{count}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div> */}

                {/* Filters */}
                {showFilters && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Input
                                type="text"
                                label="Search tickets..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                icon={<Search className="h-4 w-4" />}
                            />
                            <Select
                                label="Priority"
                                value={priorityFilter}
                                onChange={(value) => setPriorityFilter(value)}
                            >
                                <Option value="all">All Priorities</Option>
                                <Option value="low">Low</Option>
                                <Option value="medium">Medium</Option>
                                <Option value="high">High</Option>
                                <Option value="urgent">Urgent</Option>
                            </Select>
                            <Select
                                label="Category"
                                value={categoryFilter}
                                onChange={(value) => setCategoryFilter(value)}
                            >
                                <Option value="all">All Categories</Option>
                                <Option value="technical">Technical</Option>
                                <Option value="billing">Billing</Option>
                                <Option value="general">General</Option>
                                <Option value="account">Account</Option>
                                <Option value="bug_report">Bug Report</Option>
                                <Option value="feature_request">Feature Request</Option>
                            </Select>
                            <Select
                                label="Agent"
                                value={assignedAgentFilter}
                                onChange={(value) => setAssignedAgentFilter(value)}
                                disabled={agentsLoading}
                                className={agentsError ? 'border-red-500' : ''}
                            >
                                <Option value="all">All Agents</Option>
                                <Option value="unassigned">Unassigned</Option>
                                {agents?.users?.map((agent) => (
                                    <Option key={agent._id} value={agent._id}>
                                        {agent.name}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        {hasActiveFilters && (
                            <div className="mt-4 flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    {getFilteredTickets().length} of {tickets.length} tickets shown
                                </p>
                                <Button
                                    variant="text"
                                    size="sm"
                                    onClick={clearFilters}
                                    className="flex items-center gap-2"
                                >
                                    <X className="h-4 w-4" />
                                    Clear Filters
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* Kanban Board */}
                <div className="flex gap-6 overflow-x-auto pb-4">
                    {statusColumns.map((column) => (
                        <StatusColumn
                            key={column.id}
                            column={column}
                            tickets={getTicketsByStatus(column.id)}
                        />
                    ))}
                </div>

                {/* View Ticket Dialog */}
                <ModalX isOpen={viewDialogOpen} onClose={() => setViewDialogOpen(false)} title="Ticket Details" maxWidth="xxxl">
                    {selectedTicket && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Subject
                                    </Typography>
                                    <Typography variant="small" className="text-gray-600">
                                        {selectedTicket.subject}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Status
                                    </Typography>
                                    <div className="flex">
                                        <Chip
                                            value={selectedTicket.status.replace('_', ' ')}
                                            color={statusColumns.find(col => col.id === selectedTicket.status)?.color || 'gray'}
                                            size="sm"
                                            className="capitalize mt-1"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Typography variant="small" className="font-semibold text-gray-900">
                                    Description
                                </Typography>
                                <Typography variant="small" className="text-gray-600 mt-1">
                                    {selectedTicket.description}
                                </Typography>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Customer
                                    </Typography>
                                    <div className="mt-1 space-y-1">
                                        <div className="flex items-center gap-2">
                                            <User className="h-4 w-4 text-gray-500" />
                                            <Typography variant="small" className="text-gray-600">
                                                {selectedTicket.customer.name}
                                            </Typography>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Mail className="h-4 w-4 text-gray-500" />
                                            <Typography variant="small" className="text-gray-600">
                                                {selectedTicket.customer.email}
                                            </Typography>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Phone className="h-4 w-4 text-gray-500" />
                                            <Typography variant="small" className="text-gray-600">
                                                {selectedTicket.customer.phone}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Assigned Agent
                                    </Typography>
                                    <Typography variant="small" className="text-gray-600 mt-1">
                                        {selectedTicket.assignedAgent ? selectedTicket.assignedAgent.name : 'Unassigned'}
                                    </Typography>
                                </div>
                            </div>

                            {selectedTicket.resolution && (
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Resolution
                                    </Typography>
                                    <Typography variant="small" className="text-gray-600 mt-1">
                                        {selectedTicket.resolution}
                                    </Typography>
                                </div>
                            )}

                            {selectedTicket.attachments.length > 0 && (
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Attachments
                                    </Typography>
                                    <div className="mt-1 space-y-1">
                                        {selectedTicket.attachments.map((attachment, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                                <Paperclip className="h-4 w-4 text-gray-500" />
                                                <Typography variant="small" className="text-blue-600 hover:underline cursor-pointer">
                                                    {attachment.originalName}
                                                </Typography>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </ModalX>

                {/* Update Status Dialog */}
                <ModalX isOpen={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} title="Update Ticket Status">
                    <div className="space-y-4">
                        <Select
                            label="New Status"
                            value={newStatus}
                            onChange={(value) => setNewStatus(value)}
                        >
                            {statusColumns.map((status) => (
                                <Option key={status.id} value={status.id}>
                                    <div className="flex items-center gap-2">
                                        <status.icon className={`h-4 w-4 text-${status.color}-600`} />
                                        <span className="capitalize">{status.title.replace('_', ' ')}</span>
                                    </div>
                                </Option>
                            ))}
                        </Select>

                        {(newStatus === 'resolved' || newStatus === 'closed') && (
                            <Textarea
                                label="Resolution (Required)"
                                value={resolution}
                                onChange={(e) => setResolution(e.target.value)}
                                rows={4}
                            />
                        )}
                    </div>
                    <hr className='my-4' />
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="outlined"
                            color="gray"
                            onClick={() => {
                                setStatusDialogOpen(false);
                                setNewStatus('');
                                setResolution('');
                            }}
                            className="rounded-full flex items-center gap-2"
                            size="sm"
                            disabled={updateTicketStatusMutation.isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="filled"
                            color="gray"
                            onClick={submitStatusUpdate}
                            disabled={!newStatus || ((newStatus === 'resolved' || newStatus === 'closed') && !resolution) || updateTicketStatusMutation.isLoading}
                            className="rounded-full flex items-center gap-2"
                            size="sm"
                        >
                            {updateTicketStatusMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                            Update Status
                        </Button>
                    </div>
                </ModalX>

                {/* Assign Agent Dialog */}
                <ModalX isOpen={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} title="Assign Agent">
                    <div>
                        {
                            <Select
                                label="Select Agent"
                                value={selectedAgent}
                                onChange={(val) => setSelectedAgent(val)}
                                disabled={agentsLoading}
                            >
                                {/* <Option value=''>Unassigned</Option> */}
                                {agents?.users?.map((agent) => {
                                    return (
                                        <Option key={agent._id} value={agent._id}>
                                            {agent.name} ({agent.email})
                                        </Option>
                                    )
                                }
                                )}
                            </Select>

                        }

                    </div>
                    <hr className='my-4' />
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="outlined"
                            color="gray"
                            onClick={() => {
                                setAssignDialogOpen(false);
                                setSelectedAgent('');
                            }}
                            className="rounded-full flex items-center gap-2"
                            size="sm"
                            disabled={assignTicketMutation.isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="filled"
                            color="gray"
                            onClick={submitAssignment}
                            disabled={assignTicketMutation.isLoading}
                            className="rounded-full flex items-center gap-2"
                            size="sm"
                        >
                            {assignTicketMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                            Assign
                        </Button>
                    </div>
                </ModalX>

                {/* Create Ticket Dialog */}
                <ModalX isOpen={createTicketModalOpen} onClose={() => setCreateTicketModalOpen(false)} title="Create Ticket" maxWidth="xl">
                    <div className="space-y-4">
                        {
                            customersError ? (
                                <div className='text-red-500'>
                                    {customersError.message}
                                </div>
                            ) :
                                (
                                    <Autocomplete
                                        placeholder={<>Select User <span className="text-red-500 ml-1">*</span></>}
                                        isLoading={customersLoading}
                                        options={
                                            customers?.users?.map((user) => ({
                                                value: user._id,
                                                label: <div className='flex items-center justify-between gap-2'>
                                                    <div className='flex items-center gap-2'>
                                                        <div className='w-4 h-4 rounded-full'>
                                                            <UserIcon size={12} />
                                                        </div>
                                                        {user.name}
                                                    </div>
                                                    <div className='text-xs text-gray-500'>
                                                        {user.email}
                                                    </div>
                                                </div>,
                                                searchLabel: user.name + ' (' + user.email + ')'
                                            })) || []}
                                        isClearable={true}
                                        required={true}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.searchLabel}
                                        onChange={(newValue) => setNewTicketFormData({ ...newTicketFormData, customer: newValue })}
                                        value={newTicketFormData.customer}
                                    />
                                )
                        }
                        <hr />
                        <Input
                            name='subject'
                            label="Subject"
                            required
                            value={newTicketFormData.subject}
                            onChange={handleCreateTicketInputChange}
                        />
                        <Textarea
                            name='description'
                            label="Description"
                            required
                            value={newTicketFormData.description}
                            onChange={handleCreateTicketInputChange}
                            rows={6}
                        />
                        <hr />
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                            <div className='md:col-span-2'>
                                <Select
                                    name='category'
                                    label={<>Category <span className="text-red-500 ml-1">*</span></>}
                                    value={newTicketFormData.category}
                                    onChange={(value) => handleCreateTicketSelectChange('category', value)}
                                >
                                    {
                                        SUPPORT_TICKET_CATEGORIES_KEY_VALUE_ARRAY.map((category) => (
                                            <Option key={category.key} value={category.value}>
                                                <div className='flex items-center justify-between gap-2'>
                                                    <div className='flex items-center gap-2'>
                                                        <TagIcon size={12} /> {convertToPresentableText(category.value)}
                                                    </div>
                                                    <div className='text-xs text-gray-500'>
                                                        {SUPPORT_TICKET_CATEGORIES_DESCRIPTION[category.key]}
                                                    </div>
                                                </div>
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>
                            <div>
                                <Select
                                    name='priority'
                                    label={<>Priority <span className="text-red-500 ml-1">*</span></>}
                                    value={newTicketFormData.priority}
                                    onChange={(value) => handleCreateTicketSelectChange('priority', value)}
                                >
                                    {
                                        LEVELS_ENUM.map((priority) => (
                                            <Option key={priority} value={priority}>
                                                <span className='capitalize'>
                                                    {priority}
                                                </span>
                                            </Option>
                                        ))
                                    }
                                </Select>
                            </div>
                        </div>
                        <hr />
                        <div className='flex items-center justify-end gap-2'>
                            <Button variant="outlined" className='rounded-full' size='sm' color="gray" onClick={() => setCreateTicketModalOpen(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="gradient"
                                className='rounded-full'
                                size='sm'
                                onClick={handleCreateTicketSubmit}
                                disabled={
                                    createTicketByAdminMutation.isLoading ||
                                    !newTicketFormData.customer ||
                                    !newTicketFormData.subject ||
                                    !newTicketFormData.description ||
                                    !newTicketFormData.category ||
                                    !newTicketFormData.priority
                                }
                                loading={createTicketByAdminMutation.isLoading}
                            >
                                Create Ticket
                            </Button>
                        </div>
                    </div>
                </ModalX>

                {/* Set Due Date Dialog */}
                <ModalX isOpen={dueDateDialogOpen} onClose={() => setDueDateDialogOpen(false)} title="Set Due Date">
                    <div>
                        <Input
                            label="Due Date"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            required
                        />
                    </div>
                    <hr className='my-4' />
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            variant="outlined"
                            className='rounded-full'
                            size='sm'
                            color="gray"
                            onClick={() => setDueDateDialogOpen(false)}
                            disabled={updateTicketDueDateMutation.isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="filled"
                            className='rounded-full'
                            size='sm'
                            onClick={submitSetDueDate}
                            disabled={updateTicketDueDateMutation.isLoading || !dueDate}
                            loading={updateTicketDueDateMutation.isLoading}
                        >
                            Set Due Date
                        </Button>
                    </div>
                </ModalX>

                {/* No Results State */}
                {!isLoading && getFilteredTickets().length === 0 && hasActiveFilters && (
                    <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                        <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
                        <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
                        <Button variant="outlined" onClick={clearFilters}>
                            Clear all filters
                        </Button>
                    </div>
                )}

                {/* Empty State */}
                {!isLoading && tickets.length === 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                        <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No support tickets</h3>
                        <p className="text-gray-600">Support tickets will appear here when customers submit them</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupportTicketsKanban;